"use client";

import React from "react";
import { useAuth } from "@clerk/nextjs";
import { useQuery } from "@tanstack/react-query";
import { LineChart, Lock } from "lucide-react";

import { useFetchWrapper } from "modules/common/hooks/useFetchWrapper";
import { useSets } from "modules/common/hooks/useSets";
import { API_ROUTES, CHART_RESPONSE_VERSION } from "utils/api-route.constants";
import { RosenCardPriceChart } from "modules/cards/components/RosenCardPriceChart";
import { ChartSkeleton } from "modules/cards/components/ChartSkeleton";
import { SignUpButtonCTA } from "modules/auth/components/SignUpButtonCTA";
import {
  CardChartRequest,
  CardChartResponse,
  GameCard,
} from "modules/cards/model/card";
import { QUERIES } from "modules/common/query";
import { STATIC_DATA_CONFIG } from "modules/common/react-query-configs";
import {
  appendPricingParams,
  usePricingRequest,
  withPricingCacheKey,
} from "modules/common/hooks/usePricingRequest";

interface ChartSectionProps {
  cardId: string;
  card: GameCard;
}

// useCardFinishState maps the canonical nonfoil finish to "" on the wire; the chart fetch
// for the unselected default uses the empty string so the cache key lines up
// with what useCardChart asks for once the user lands on the page.
const PRIMARY_FINISH_API_VALUE = "";

function NoPriceHistory() {
  return (
    <aside aria-label="Price information">
      <div className="card my-4 shadow-md">
        <div className="card-body items-center py-6 text-center">
          <p className="text-base-content/70">
            No price history is available for this card.
          </p>
        </div>
      </div>
    </aside>
  );
}

function ChartLoadError() {
  return (
    <aside aria-label="Price information">
      <div className="card my-4 shadow-md">
        <div className="card-body items-center py-6 text-center">
          <p className="text-base-content/70">
            Couldn&apos;t load price history right now. Please try again later.
          </p>
        </div>
      </div>
    </aside>
  );
}

function SignedOutTeaser() {
  return (
    <aside aria-label="Price information">
      <div className="card bg-base-100 border-base-content/10 border shadow-sm">
        <div className="card-body items-center gap-4 py-10 text-center">
          <div className="bg-primary/10 text-primary relative rounded-full p-3">
            <LineChart className="h-6 w-6" />
            <span className="bg-base-100 text-base-content/70 absolute -bottom-1 -right-1 rounded-full p-1">
              <Lock className="h-3 w-3" />
            </span>
          </div>
          <div className="space-y-1">
            <h3 className="text-base font-semibold">Unlock the price chart</h3>
            <p className="text-base-content/70 max-w-xs text-sm">
              Sign up to track historical prices and see trade trends across
              local stores.
            </p>
          </div>
          <SignUpButtonCTA variant="primary" size="sm">
            Sign up to view chart
          </SignUpButtonCTA>
        </div>
      </div>
    </aside>
  );
}

export function ChartSection({ cardId, card }: ChartSectionProps) {
  const { isLoaded, isSignedIn } = useAuth();
  const fetchWrapper = useFetchWrapper();
  const { pricing, cacheKey } = usePricingRequest();
  const enabled = isLoaded && Boolean(isSignedIn);

  const primaryRequest: CardChartRequest = {
    cardId,
    ...pricing,
    finish: PRIMARY_FINISH_API_VALUE,
  };

  const chartQuery = useQuery<CardChartResponse>({
    queryKey: withPricingCacheKey(
      [QUERIES.MAGIC_CARDS_CHART, primaryRequest],
      cacheKey
    ),
    queryFn: () => {
      const params = new URLSearchParams({
        finish: primaryRequest.finish,
        v: CHART_RESPONSE_VERSION,
      });
      appendPricingParams(params, pricing);
      return fetchWrapper.get(
        `${API_ROUTES.MAGIC_CARDS.CHART(cardId)}?${params.toString()}`,
        null
      );
    },
    enabled,
    ...STATIC_DATA_CONFIG,
    retry: false,
  });

  const { query: setsQuery } = useSets();

  if (!isLoaded || !isSignedIn) {
    return <SignedOutTeaser />;
  }

  if (chartQuery.isLoading || setsQuery.isLoading) {
    return <ChartSkeleton />;
  }

  if (chartQuery.isError) {
    return <ChartLoadError />;
  }

  const chartData = chartQuery.data;
  if (
    !chartData ||
    (chartData.availableFinishes.length === 0 && chartData.chart === null)
  ) {
    return <NoPriceHistory />;
  }

  return (
    <aside aria-label="Price information">
      <RosenCardPriceChart
        card={card}
        availableCharts={chartData.availableFinishes}
        sets={setsQuery.data ?? {}}
      />
    </aside>
  );
}
